import React from 'react';
import { DEFAULT_THEME, useTheme } from '../theme/theme';

// Settings → General → Appearance: the per-person dashboard theme. Two
// preview cards (each a miniature of the sidebar, a primary button and an
// active tab in that theme) act as one radio group; the choice applies at
// once, is remembered in this browser, and is saved to the person's
// preferences so it follows them (theme/theme.js). Green — Almond — is the
// default and comes first.

const OPTIONS = [
  {
    id: 'green',
    title: 'Green',
    description: 'Almond: forest sidebar, deep-green buttons and green accents.',
    swatch: { sidebar: '#06110B', active: '#5fae78', activeBg: 'rgba(95,174,120,0.14)', button: '#143D26', tab: '#2E8540', light: '#DCEFE0' },
  },
  {
    id: 'blue',
    title: 'Classic blue',
    description: 'The original CognitiveUX look.',
    swatch: { sidebar: '#0d1b36', active: '#ffffff', activeBg: '#243d6b', button: '#132848', tab: '#2563eb', light: '#eff6ff' },
  },
];

const s = {
  // Padded like the other Settings tabs (the tab panel itself has none).
  wrap: { maxWidth: '760px', padding: '24px 24px 28px' },
  intro: { fontSize: '14px', color: '#475569', margin: '0 0 18px', lineHeight: 1.5 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' },
  card: (selected) => ({
    textAlign: 'left', cursor: 'pointer', background: '#fff', padding: '12px',
    border: `2px solid ${selected ? 'var(--primary, #0A0A0A)' : '#e2e8f0'}`, borderRadius: '12px',
    boxShadow: selected ? '0 4px 14px rgba(15,23,42,0.10)' : '0 1px 2px rgba(15,23,42,0.05)',
    transition: 'border-color 0.15s, box-shadow 0.15s', font: 'inherit',
  }),
  preview: { display: 'grid', gridTemplateColumns: '64px 1fr', height: '120px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' },
  side: (bg) => ({ background: bg, padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: '6px' }),
  sideLine: (bg, w = '100%') => ({ height: '7px', width: w, borderRadius: '4px', background: bg }),
  main: { background: '#f5f7f9', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' },
  tabs: { display: 'flex', gap: '10px', alignItems: 'flex-end', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px' },
  tab: (color, active) => ({ height: '6px', width: '34px', borderRadius: '3px', background: active ? color : '#cbd5e1', boxShadow: active ? `0 5px 0 -3px ${color}` : 'none' }),
  btnRow: { display: 'flex', gap: '8px', marginTop: 'auto' },
  btn: (bg) => ({ height: '18px', width: '52px', borderRadius: '5px', background: bg }),
  ghost: { height: '18px', width: '44px', borderRadius: '5px', border: '1px solid rgba(10,10,10,0.14)', background: 'transparent' },
  util: (bg) => ({ height: '18px', width: '40px', borderRadius: '5px', background: bg }),
  foot: { display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px' },
  radio: (selected) => ({
    width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0,
    border: `2px solid ${selected ? 'var(--primary, #0A0A0A)' : '#94a3b8'}`,
    boxShadow: selected ? 'inset 0 0 0 3px #fff, inset 0 0 0 9px var(--primary, #0A0A0A)' : 'none',
  }),
  title: { fontSize: '14px', fontWeight: 600, color: '#0f172a' },
  badge: {
    display: 'inline-block', marginLeft: '8px', padding: '1px 8px', borderRadius: '99px', fontSize: '11px', fontWeight: 600,
    verticalAlign: 'middle', background: 'var(--cux-utility-bg, #eff6ff)', color: 'var(--cux-utility-text, #1d4ed8)',
  },
  desc: { fontSize: '12.5px', color: '#64748b' },
};

function Preview({ swatch }) {
  return (
    <div style={s.preview} aria-hidden="true">
      <div style={s.side(swatch.sidebar)}>
        <div style={s.sideLine('rgba(255,255,255,0.35)', '70%')} />
        <div style={{ ...s.sideLine(swatch.activeBg), display: 'flex', alignItems: 'center', padding: '0 4px', height: '12px' }}>
          <div style={s.sideLine(swatch.active, '80%')} />
        </div>
        <div style={s.sideLine('rgba(255,255,255,0.25)', '85%')} />
        <div style={s.sideLine('rgba(255,255,255,0.25)', '60%')} />
      </div>
      <div style={s.main}>
        <div style={s.tabs}>
          <div style={s.tab(swatch.tab, true)} />
          <div style={s.tab(swatch.tab, false)} />
          <div style={s.tab(swatch.tab, false)} />
        </div>
        <div style={s.btnRow}>
          <div style={s.btn(swatch.button)} />
          <div style={s.ghost} />
          <div style={s.util(swatch.light)} />
        </div>
      </div>
    </div>
  );
}

export default function AppearanceSettings() {
  const [theme, setTheme] = useTheme();
  return (
    <div style={s.wrap} data-testid="appearance-settings">
      <p style={s.intro}>Choose how the dashboard looks for you. The change applies right away and follows you to other browsers.</p>
      <div style={s.grid} role="radiogroup" aria-label="Dashboard theme">
        {OPTIONS.map((o) => {
          const selected = theme === o.id;
          return (
            <button
              key={o.id}
              type="button"
              role="radio"
              aria-checked={selected}
              style={s.card(selected)}
              onClick={() => { if (!selected) setTheme(o.id); }}
            >
              <Preview swatch={o.swatch} />
              <span style={s.foot}>
                <span style={s.radio(selected)} />
                <span>
                  <span style={{ ...s.title, display: 'block' }}>
                    {o.title}{o.id === DEFAULT_THEME && <span style={s.badge}>Default</span>}
                  </span>
                  <span style={s.desc}>{o.description}</span>
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
